import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

interface InspectorCardData {
  employeeId: string;
  role: string;
  isActive: boolean;
  totalLogins: number;
  lastLogin: string | null;
  totalMinutes: number;
  lastUsageMinutes: number;
  totalInspections: number;
  containerInspections: number;
  pilotInspections: number;
  fabricInspections: number;
  lineInspections: number;
  lastInspectionActive: string | null;
}

interface InspectorHoverCardProps {
  inspectorId: string | null;
  name: string;
  initials: string;
  color: string;
  photo?: string;
  token: string | null;
  children: React.ReactNode;
}

const fmtMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
};

const fmtDate = (iso: string | null) => {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const InspectorHoverCard: React.FC<InspectorHoverCardProps> = ({
  inspectorId,
  name,
  initials,
  color,
  photo,
  token,
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<InspectorCardData | null>(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cacheRef = useRef<InspectorCardData | 'deleted' | null>(null);

  const handleMouseEnter = useCallback(async () => {
    timerRef.current = setTimeout(async () => {
      if (!wrapperRef.current) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const cardWidth = 340;
      const cardHeight = 530;

      let left = rect.right + 10;
      if (left + cardWidth > window.innerWidth - 20) {
        left = rect.left - cardWidth - 10;
      }
      if (left < 8) left = 8;

      let top = rect.top - 20;
      if (top + cardHeight > window.innerHeight - 20) {
        top = window.innerHeight - cardHeight - 20;
      }
      if (top < 8) top = 8;

      setPopupPos({ top, left });
      setIsVisible(true);

      // Use cache if available
      if (cacheRef.current !== null) {
        if (cacheRef.current === 'deleted') {
          setData(null);
        } else {
          setData(cacheRef.current as InspectorCardData);
        }
        return;
      }

      if (!inspectorId) {
        cacheRef.current = 'deleted';
        setData(null);
        return;
      }

      setIsLoading(true);
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const [userRes, usageRes, statsRes] = await Promise.all([
          fetch(`${baseUrl}/api/v1/users/${inspectorId}`, { headers }),
          fetch(`${baseUrl}/api/v1/users/usage/${inspectorId}`),
          fetch(`${baseUrl}/api/v1/users/${inspectorId}/inspection-stats`, { headers }),
        ]);

        const [user, usage, stats] = await Promise.all([
          userRes.json(),
          usageRes.json(),
          statsRes.json(),
        ]);

        const cardData: InspectorCardData = {
          employeeId: user.data?.employee_id || 'N/A',
          role: user.data?.role || 'user',
          isActive: user.data?.is_active ?? true,
          totalLogins: usage.total_logins || 0,
          lastLogin: usage.last_login || null,
          totalMinutes: usage.total_minutes || 0,
          lastUsageMinutes: usage.last_usage_minutes || 0,
          totalInspections: stats.data?.total || 0,
          containerInspections: stats.data?.by_type?.container || 0,
          pilotInspections: stats.data?.by_type?.pilot || 0,
          fabricInspections: stats.data?.by_type?.fabric || 0,
          lineInspections: stats.data?.by_type?.line || 0,
          lastInspectionActive: stats.data?.last_active || null,
        };

        cacheRef.current = cardData;
        setData(cardData);
      } catch (err) {
        console.error('InspectorHoverCard: failed to fetch inspector data', err);
      } finally {
        setIsLoading(false);
      }
    }, 2000);
  }, [inspectorId, token]);

  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
  }, []);

  const popup =
    isVisible
      ? ReactDOM.createPortal(
          <div
            className="fixed z-[9999] w-[340px] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden pointer-events-none animate-in fade-in zoom-in-95 duration-150"
            style={{ top: popupPos.top, left: popupPos.left }}
          >
            {/* Header */}
            <div className="p-5 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white">
              <div className="flex items-start gap-3">
                {photo ? (
                  <img
                    src={photo}
                    alt={name}
                    className="h-12 w-12 rounded-full object-cover ring-2 ring-white shadow-md flex-shrink-0"
                  />
                ) : (
                  <div
                    className={`h-12 w-12 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ring-2 ring-white shadow-md ${color}`}
                  >
                    {initials}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-900 leading-tight">{name}</p>
                  {inspectorId ? (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {data
                        ? `${data.role === 'admin' ? 'Admin' : 'User'} • ${data.employeeId}`
                        : isLoading
                        ? '...'
                        : '—'}
                    </p>
                  ) : (
                    <p className="text-xs text-red-500 font-semibold mt-0.5">Account Deleted</p>
                  )}
                </div>
                {inspectorId && data && (
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold ring-1 flex-shrink-0 ${
                      data.isActive
                        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                        : 'bg-slate-100 text-slate-500 ring-slate-200'
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        data.isActive ? 'bg-emerald-500' : 'bg-slate-400'
                      }`}
                    ></span>
                    {data.isActive ? 'Active' : 'Inactive'}
                  </span>
                )}
              </div>
            </div>

            {/* Body */}
            {isLoading ? (
              <div className="flex items-center justify-center py-10 gap-2 text-slate-400 text-sm">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-[#136bce]"></div>
                Loading details...
              </div>
            ) : !inspectorId ? (
              <div className="p-6 text-center">
                <span className="material-symbols-outlined text-slate-300 text-[44px] block mb-2">
                  person_off
                </span>
                <p className="text-sm font-semibold text-slate-600">User account deleted</p>
                <p className="text-xs text-slate-400 mt-1">
                  Historical inspection data is preserved.
                </p>
              </div>
            ) : data ? (
              <div className="p-5 space-y-5">
                {/* Usage Analytics */}
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400 mb-2.5">
                    Usage Analytics
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                      <p className="text-[10px] font-medium text-slate-400 flex items-center gap-1 mb-1">
                        <span className="material-symbols-outlined text-[12px]">login</span>
                        Total Logins
                      </p>
                      <p className="text-xl font-bold text-slate-800">{data.totalLogins}</p>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                      <p className="text-[10px] font-medium text-slate-400 flex items-center gap-1 mb-1">
                        <span className="material-symbols-outlined text-[12px]">schedule</span>
                        Total Minutes used
                      </p>
                      <p className="text-xl font-bold text-slate-800">
                        {fmtMinutes(data.totalMinutes)}
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                      <p className="text-[10px] font-medium text-slate-400 flex items-center gap-1 mb-1">
                        <span className="material-symbols-outlined text-[12px]">timer</span>
                        Last Session Length
                      </p>
                      <p className="text-xl font-bold text-slate-800">
                        {fmtMinutes(data.lastUsageMinutes)}
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                      <p className="text-[10px] font-medium text-slate-400 flex items-center gap-1 mb-1">
                        <span className="material-symbols-outlined text-[12px]">calendar_today</span>
                        Last Login Date
                      </p>
                      <p className="text-[11px] font-bold text-slate-800 leading-tight">
                        {fmtDate(data.lastLogin)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Inspection Analytics */}
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400 mb-2.5">
                    Inspection Analytics
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="rounded-xl bg-slate-50 px-3 py-2.5 text-center col-span-2">
                      <p className="text-xl font-bold text-slate-800">
                        {data.totalInspections}
                      </p>
                      <p className="text-[10px] font-medium text-slate-400 mt-0.5">Total Inspections</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-xl bg-blue-50 px-3 py-2.5 text-center">
                      <p className="text-xl font-bold text-blue-600">
                        {data.containerInspections}
                      </p>
                      <p className="text-[10px] font-medium text-slate-400 mt-0.5">Container</p>
                    </div>
                    <div className="rounded-xl bg-purple-50 px-3 py-2.5 text-center">
                      <p className="text-xl font-bold text-purple-600">
                        {data.pilotInspections}
                      </p>
                      <p className="text-[10px] font-medium text-slate-400 mt-0.5">Pilot</p>
                    </div>
                    <div className="rounded-xl bg-emerald-50 px-3 py-2.5 text-center">
                      <p className="text-xl font-bold text-emerald-600">
                        {data.fabricInspections}
                      </p>
                      <p className="text-[10px] font-medium text-slate-400 mt-0.5">Fabric</p>
                    </div>
                    <div className="rounded-xl bg-amber-50 px-3 py-2.5 text-center">
                      <p className="text-xl font-bold text-amber-600">
                        {data.lineInspections}
                      </p>
                      <p className="text-[10px] font-medium text-slate-400 mt-0.5">Line</p>
                    </div>
                  </div>
                  {data.lastInspectionActive && (
                    <p className="text-[10px] text-slate-400 mt-2.5">
                      Last inspection activity:{' '}
                      <span className="font-semibold text-slate-600">
                        {fmtDate(data.lastInspectionActive)}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            ) : null}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      ref={wrapperRef}
      className="inline-flex items-center gap-2 font-medium"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {popup}
    </div>
  );
};

export default InspectorHoverCard;
